import {
	TerraDrawMouseEvent,
	TerraDrawKeyboardEvent,
	SELECT_PROPERTIES,
	TerraDrawAdapterStyling,
	HexColorStyling,
	NumericStyling,
	Cursor,
	Validation,
	UpdateTypes,
	Z_INDEX,
	Snapping,
	UrlStyling,
	COMMON_PROPERTIES,
	MARKER_URL_DEFAULT,
	FinishActions,
} from "../../common";
import { LineString, Point, Polygon, Position } from "geojson";
import {
	BaseModeOptions,
	CustomStyling,
	ModeUpdateOptions,
	TerraDrawBaseSelectMode,
} from "../base.mode";
import { MidPointBehavior } from "./behaviors/midpoint.behavior";
import {
	SelectionPointBehavior,
	SelectionPointProperties,
} from "./behaviors/selection-point.behavior";
import { FeatureAtPointerEventBehavior } from "./behaviors/feature-at-pointer-event.behavior";
import { PixelDistanceBehavior } from "../pixel-distance.behavior";
import { ClickBoundingBoxBehavior } from "../click-bounding-box.behavior";
import { DragFeatureBehavior } from "./behaviors/drag-feature.behavior";
import { DragCoordinateBehavior } from "./behaviors/drag-coordinate.behavior";
import { BehaviorConfig } from "../base.behavior";
import { RotateFeatureBehavior } from "./behaviors/rotate-feature.behavior";
import { ScaleFeatureBehavior } from "./behaviors/scale-feature.behavior";
import { FeatureId, GeoJSONStoreFeatures } from "../../store/store";
import { getDefaultStyling } from "../../util/styling";
import {
	DragCoordinateResizeBehavior,
	ResizeOptions,
} from "./behaviors/drag-coordinate-resize.behavior";
import { CoordinatePointBehavior } from "./behaviors/coordinate-point.behavior";
import { CoordinateSnappingBehavior } from "../coordinate-snapping.behavior";
import { LineSnappingBehavior } from "../line-snapping.behavior";
import { MutateFeatureBehavior, Mutations } from "../mutate-feature.behavior";
import { ReadFeatureBehavior } from "../read-feature.behavior";

type TerraDrawSelectModeKeyEvents = {
	deselect: KeyboardEvent["key"] | null;
	delete: KeyboardEvent["key"] | null;
	rotate: KeyboardEvent["key"][] | null;
	scale: KeyboardEvent["key"][] | null;
};

const defaultKeyEvents = {
	deselect: "Escape",
	delete: "Delete",
	rotate: ["Control", "r"],
	scale: ["Control", "s"],
};

type ModeFlags = {
	feature?: {
		validation?: Validation;
		draggable?: boolean;
		rotateable?: boolean;
		scaleable?: boolean;
		selfIntersectable?: boolean;
		coordinates?: {
			snappable?: boolean | Snapping;
			midpoints?:
				| boolean
				| {
						draggable?: boolean;
				  };
			draggable?: boolean;
			resizable?: ResizeOptions;
			deletable?: boolean;
		};
	};
};

type SelectionStyling = {
	// Point
	selectedPointColor: HexColorStyling;
	selectedPointWidth: NumericStyling;
	selectedPointOpacity: NumericStyling;
	selectedPointOutlineColor: HexColorStyling;
	selectedPointOutlineWidth: NumericStyling;
	selectedPointOutlineOpacity: NumericStyling;

	// Marker
	selectedMarkerUrl: UrlStyling;
	selectedMarkerHeight: NumericStyling;
	selectedMarkerWidth: NumericStyling;

	// LineString
	selectedLineStringColor: HexColorStyling;
	selectedLineStringWidth: NumericStyling;
	selectedLineStringOpacity: NumericStyling;

	// Polygon
	selectedPolygonColor: HexColorStyling;
	selectedPolygonFillOpacity: NumericStyling;
	selectedPolygonOutlineColor: HexColorStyling;
	selectedPolygonOutlineOpacity: NumericStyling;
	selectedPolygonOutlineWidth: NumericStyling;

	// Selection Points (points at vertices of a polygon/linestring feature)
	selectionPointWidth: NumericStyling;
	selectionPointColor: HexColorStyling;
	selectionPointOpacity: NumericStyling;
	selectionPointOutlineColor: HexColorStyling;
	selectionPointOutlineWidth: NumericStyling;
	selectionPointOutlineOpacity: NumericStyling;

	// Mid points (points at mid point of a polygon/linestring feature)
	midPointColor: HexColorStyling;
	midPointOutlineColor: HexColorStyling;
	midPointOpacity: NumericStyling;
	midPointWidth: NumericStyling;
	midPointOutlineWidth: NumericStyling;
	midPointOutlineOpacity: NumericStyling;
};

interface Cursors {
	pointerOver?: Cursor;
	dragStart?: Cursor;
	dragEnd?: Cursor;
	insertMidpoint?: Cursor;
}

const defaultCursors = {
	pointerOver: "move",
	dragStart: "move",
	dragEnd: "move",
	insertMidpoint: "crosshair",
} as Required<Cursors>;

interface TerraDrawSelectModeOptions<T extends CustomStyling>
	extends BaseModeOptions<T> {
	pointerDistance?: number;
	flags?: { [mode: string]: ModeFlags };
	keyEvents?: TerraDrawSelectModeKeyEvents | null;
	dragEventThrottle?: number;
	cursors?: Cursors;
	allowManualDeselection?: boolean;
}

export class TerraDrawSelectMode extends TerraDrawBaseSelectMode<SelectionStyling> {
	public mode = "select";

	private allowManualDeselection = true;
	private dragEventThrottle = 5;
	private dragEventCount = 0;
	private selected: FeatureId[] = [];

	private flags: { [mode: string]: ModeFlags } = {};
	private keyEvents: TerraDrawSelectModeKeyEvents = defaultKeyEvents;
	private cursors: Required<Cursors> = defaultCursors;
	private validations: Record<string, Validation> = {};

	// Behaviors
	private selectionPoints!: SelectionPointBehavior;
	private midPoints!: MidPointBehavior;
	private coordinateSnap!: CoordinateSnappingBehavior;
	private featuresAtMouseEvent!: FeatureAtPointerEventBehavior;
	private pixelDistance!: PixelDistanceBehavior;
	private clickBoundingBox!: ClickBoundingBoxBehavior;
	private dragFeature!: DragFeatureBehavior;
	private dragCoordinate!: DragCoordinateBehavior;
	private rotateFeature!: RotateFeatureBehavior;
	private scaleFeature!: ScaleFeatureBehavior;
	private dragCoordinateResizeFeature!: DragCoordinateResizeBehavior;
	private coordinatePoints!: CoordinatePointBehavior;
	private lineSnap!: LineSnappingBehavior;
	private mutateFeature!: MutateFeatureBehavior;
	private readFeature!: ReadFeatureBehavior;

	constructor(options?: TerraDrawSelectModeOptions<SelectionStyling>) {
		super(options, true);
		this.updateOptions(options);
	}

	override updateOptions(
		options?: ModeUpdateOptions<TerraDrawSelectModeOptions<SelectionStyling>>,
	) {
		super.updateOptions(options);

		if (options && options.cursors) {
			this.cursors = { ...this.cursors, ...options.cursors };
		} else {
			this.cursors = defaultCursors;
		}

		// We want to have some defaults, but also allow key bindings
		// to be explicitly turned off
		if (options?.keyEvents === null) {
			this.keyEvents = {
				deselect: null,
				delete: null,
				rotate: null,
				scale: null,
			};
		} else if (options?.keyEvents) {
			this.keyEvents = { ...this.keyEvents, ...options.keyEvents };
		}

		if (options?.dragEventThrottle !== undefined) {
			this.dragEventThrottle = options.dragEventThrottle;
		}

		if (options?.allowManualDeselection !== undefined) {
			this.allowManualDeselection = options.allowManualDeselection;
		}

		// Flags and Validations
		if (options?.flags) {
			this.flags = { ...this.flags, ...options.flags };
			this.validations = {};

			for (const mode in this.flags) {
				const feature = this.flags[mode].feature;
				if (feature && feature.validation) {
					this.validations[mode] = feature.validation;
				}
			}
		}
	}

	selectFeature(featureId: FeatureId) {
		this.select(featureId, false);
	}

	setSelecting() {
		if (this._state === "started") {
			this._state = "selecting";
		} else {
			throw new Error("Mode must be started to move to selecting state");
		}
	}

	registerBehaviors(config: BehaviorConfig) {
		this.readFeature = new ReadFeatureBehavior(config);
		this.mutateFeature = new MutateFeatureBehavior(config, {
			validate: (feature, context) => {
				const mode = feature.properties.mode as string;

				// By default all changes are valid
				if (!this.validations || !this.validations[mode]) {
					return { valid: true };
				}

				return this.validations[mode](feature, context);
			},
		});

		this.pixelDistance = new PixelDistanceBehavior(config);
		this.clickBoundingBox = new ClickBoundingBoxBehavior(config);
		this.featuresAtMouseEvent = new FeatureAtPointerEventBehavior(
			config,
			this.clickBoundingBox,
			this.pixelDistance,
		);

		this.selectionPoints = new SelectionPointBehavior(
			config,
			this.mutateFeature,
		);
		this.coordinatePoints = new CoordinatePointBehavior(
			config,
			this.readFeature,
			this.mutateFeature,
		);
		this.midPoints = new MidPointBehavior(
			config,
			this.selectionPoints,
			this.coordinatePoints,
			this.mutateFeature,
			this.readFeature,
		);
		this.coordinateSnap = new CoordinateSnappingBehavior(
			config,
			this.pixelDistance,
			this.clickBoundingBox,
		);
		this.lineSnap = new LineSnappingBehavior(
			config,
			this.pixelDistance,
			this.clickBoundingBox,
		);
		this.rotateFeature = new RotateFeatureBehavior(
			config,
			this.selectionPoints,
			this.midPoints,
			this.coordinatePoints,
			this.readFeature,
			this.mutateFeature,
		);

		this.dragFeature = new DragFeatureBehavior(
			config,
			this.featuresAtMouseEvent,
			this.selectionPoints,
			this.midPoints,
			this.coordinatePoints,
			this.readFeature,
			this.mutateFeature,
		);
		this.dragCoordinate = new DragCoordinateBehavior(
			config,
			this.pixelDistance,
			this.selectionPoints,
			this.midPoints,
			this.coordinatePoints,
			this.coordinateSnap,
			this.lineSnap,
			this.readFeature,
			this.mutateFeature,
		);
		this.dragCoordinateResizeFeature = new DragCoordinateResizeBehavior(
			config,
			this.pixelDistance,
			this.selectionPoints,
			this.midPoints,
			this.coordinatePoints,
			this.readFeature,
			this.mutateFeature,
		);
		this.scaleFeature = new ScaleFeatureBehavior(
			config,
			this.dragCoordinateResizeFeature,
		);
	}

	public deselectFeature() {
		this.deselect();
	}

	private deselect() {
		this.mutateFeature.setDeselected(this.selected);

		this.onDeselect(this.selected[0]);
		this.selected = [];
		this.selectionPoints.delete();
		this.midPoints.delete();
	}

	private deleteSelected() {
		// Delete all selected features
		// from the store and clear selected
		// We don't need to set selected false
		// as we're going to delete the feature

		if (this.selected.length) {
			this.mutateFeature.deleteFeaturesIfPresent(this.selected);
		}

		this.selected = [];
	}

	private onRightClick(event: TerraDrawMouseEvent) {
		if (!this.selectionPoints.ids.length) {
			return;
		}

		let clickedSelectionPointProps: SelectionPointProperties | undefined;

		let clickedFeatureDistance = Infinity;

		this.selectionPoints.ids.forEach((id) => {
			const geometry = this.readFeature.getGeometry<Point>(id);
			const distance = this.pixelDistance.measure(event, geometry.coordinates);

			if (
				distance < this.pointerDistance &&
				distance < clickedFeatureDistance
			) {
				clickedFeatureDistance = distance;
				clickedSelectionPointProps = this.readFeature.getProperties(
					id,
				) as SelectionPointProperties;
			}
		});

		if (!clickedSelectionPointProps) {
			return;
		}

		const featureId = clickedSelectionPointProps.selectionPointFeatureId;
		const coordinateIndex = clickedSelectionPointProps.index;

		// We allow for preventing deleting coordinates via flags
		const properties = this.readFeature.getProperties(featureId);
		const modeFlags = this.flags[properties.mode as string];

		// Check if we can actually delete the coordinate
		const cannotDelete =
			!modeFlags ||
			!modeFlags.feature ||
			!modeFlags.feature.coordinates ||
			!modeFlags.feature.coordinates.deletable;

		if (cannotDelete) {
			return;
		}

		const geometry = this.readFeature.getGeometry(featureId);

		let coordinates: Position[];
		if (geometry.type === "Polygon") {
			coordinates = geometry.coordinates[0];

			// Prevent creating an invalid polygon
			if (coordinates.length <= 4) {
				return;
			}
		} else if (geometry.type === "LineString") {
			coordinates = geometry.coordinates;

			// Prevent creating an invalid linestring
			if (coordinates.length <= 2) {
				return;
			}
		} else {
			return;
		}

		const isFinalPolygonCoordinate =
			geometry.type === "Polygon" &&
			(coordinateIndex === 0 || coordinateIndex === coordinates.length - 1);

		if (isFinalPolygonCoordinate) {
			// Deleting the final coordinate in a polygon breaks it
			// because GeoJSON expects a duplicate, so we need to fix
			// it by adding the new first coordinate to the end
			coordinates.shift();
			coordinates.pop();
			coordinates.push([coordinates[0][0], coordinates[0][1]]);
		} else {
			// Remove coordinate from array
			coordinates.splice(coordinateIndex, 1);
		}

		let updated: GeoJSONStoreFeatures<Polygon | LineString> | null = null;

		if (geometry.type === "Polygon") {
			updated = this.mutateFeature.updatePolygon({
				featureId,
				coordinateMutations: {
					type: Mutations.Replace,
					coordinates: [coordinates],
				},
				context: {
					updateType: UpdateTypes.Commit,
				},
			});
		} else if (geometry.type === "LineString") {
			updated = this.mutateFeature.updateLineString({
				featureId,
				coordinateMutations: {
					type: Mutations.Replace,
					coordinates: coordinates,
				},
				context: {
					updateType: UpdateTypes.Commit,
				},
			});
		}

		if (!updated) {
			return;
		}

		const featureCoordinates = updated.geometry.coordinates;

		this.mutateFeature.deleteFeaturesIfPresent([
			...this.midPoints.ids,
			...this.selectionPoints.ids,
		]);

		if (properties.coordinatePointIds) {
			this.coordinatePoints.createOrUpdate({ featureId, featureCoordinates });
		}

		this.selectionPoints.create({
			featureCoordinates,
			featureId,
		});

		if (
			modeFlags &&
			modeFlags.feature &&
			modeFlags.feature.coordinates &&
			modeFlags.feature.coordinates.midpoints
		) {
			this.midPoints.create({ featureCoordinates, featureId });
		}

		this.onFinish(featureId, {
			action: FinishActions.DeleteCoordinate,
			mode: this.mode,
		});
	}

	private select(featureId: FeatureId, fromCursor = true) {
		if (this.selected[0] === featureId) {
			return;
		}

		const { mode } = this.readFeature.getProperties(featureId);

		// This will be undefined for points
		const modeFlags = this.flags[mode as string];

		// If feature is not selectable then return
		if (!modeFlags || !modeFlags.feature) {
			return;
		}

		const previouslySelectedId = this.selected[0];

		// If we have something currently selected
		if (previouslySelectedId) {
			// If it matches the current selected feature id, do nothing
			if (previouslySelectedId === featureId) {
				return;
			} else {
				// If it's a different feature set selected
				// to false on previously selected feature
				this.deselect();
			}
		}

		if (fromCursor) {
			this.setCursor(this.cursors.pointerOver);
		}

		// Select feature
		this.selected = [featureId];

		this.mutateFeature.setSelected(featureId);

		this.onSelect(featureId);

		// Get the clicked feature
		const { type, coordinates: featureCoordinates } =
			this.readFeature.getGeometry(featureId);

		if (type !== "LineString" && type !== "Polygon") {
			return;
		}

		if (featureCoordinates && modeFlags && modeFlags.feature.coordinates) {
			this.selectionPoints.create({
				featureCoordinates,
				featureId,
			});

			if (modeFlags.feature.coordinates.midpoints) {
				this.midPoints.create({
					featureCoordinates,
					featureId,
				});
			}
		}
	}

	private onLeftClick(event: TerraDrawMouseEvent) {
		const { clickedFeature, clickedMidPoint } = this.featuresAtMouseEvent.find(
			event,
			this.selected.length > 0,
		);

		if (this.selected.length && clickedMidPoint) {
			// TODO: We probably want to make sure the midpoint
			// is visible?

			this.midPoints.insert({
				featureId: this.selected[0],
				midPointId: clickedMidPoint.id as FeatureId,
			});

			this.onFinish(this.selected[0], {
				action: FinishActions.InsertMidpoint,
				mode: this.mode,
			});

			return;
		}

		if (clickedFeature && clickedFeature.id) {
			this.select(clickedFeature.id, true);
		} else if (this.selected.length && this.allowManualDeselection) {
			this.deselect();
			return;
		}
	}

	/** @internal */
	start() {
		this.setStarted();
		this.setSelecting();
	}

	/** @internal */
	stop() {
		this.cleanUp();
		this.setStarted();
		this.setStopped();
	}

	/** @internal */
	onClick(event: TerraDrawMouseEvent) {
		if (
			(event.button === "right" &&
				this.allowPointerEvent(this.pointerEvents.rightClick, event)) ||
			(event.isContextMenu &&
				this.allowPointerEvent(this.pointerEvents.contextMenu, event))
		) {
			this.onRightClick(event);
		} else if (
			event.button === "left" &&
			this.allowPointerEvent(this.pointerEvents.leftClick, event)
		) {
			this.onLeftClick(event);
		}
	}

	private canScale(event: TerraDrawKeyboardEvent | TerraDrawMouseEvent) {
		return (
			this.keyEvents.scale &&
			this.keyEvents.scale.every((key) => event.heldKeys.includes(key))
		);
	}

	private canRotate(event: TerraDrawKeyboardEvent | TerraDrawMouseEvent) {
		return (
			this.keyEvents.rotate &&
			this.keyEvents.rotate.every((key) => event.heldKeys.includes(key))
		);
	}

	private preventDefaultKeyEvent(event: TerraDrawKeyboardEvent) {
		const isRotationKeys = this.canRotate(event);
		const isScaleKeys = this.canScale(event);

		// If we are deliberately rotating or scaling then prevent default
		if (isRotationKeys || isScaleKeys) {
			event.preventDefault();
		}
	}

	/** @internal */
	onKeyDown(event: TerraDrawKeyboardEvent) {
		this.preventDefaultKeyEvent(event);
	}

	/** @internal */
	onKeyUp(event: TerraDrawKeyboardEvent) {
		this.preventDefaultKeyEvent(event);

		if (this.keyEvents.delete && event.key === this.keyEvents.delete) {
			if (!this.selected.length) {
				return;
			}

			const selectedId = this.selected[0];

			// We are technically deselecting
			// because the selected feature is deleted
			// and will no longer exist or be selected
			const previouslySelected = this.selected[0];
			this.onDeselect(previouslySelected);

			// Delete coordinate point first if they are present, this needs
			// to be done before deleting the feature
			this.coordinatePoints.deletePointsByFeatureIds([selectedId]);

			// Delete all selected features
			this.deleteSelected();

			// Remove all selection points
			this.selectionPoints.delete();
			this.midPoints.delete();
		} else if (
			this.keyEvents.deselect &&
			event.key === this.keyEvents.deselect
		) {
			this.cleanUp();
		}
	}

	/** @internal */
	cleanUp() {
		if (this.selected.length) {
			this.deselect();
		}
	}

	/** @internal */
	onDragStart(
		event: TerraDrawMouseEvent,
		setMapDraggability: (enabled: boolean) => void,
	) {
		if (!this.allowPointerEvent(this.pointerEvents.onDragStart, event)) {
			return;
		}

		// We only need to stop the map dragging if
		// we actually have something selected
		if (!this.selected.length) {
			return;
		}

		// If the selected feature is not draggable
		// don't do anything
		const properties = this.readFeature.getProperties(this.selected[0]);
		const modeFlags = this.flags[properties.mode as string];
		const draggable =
			modeFlags &&
			modeFlags.feature &&
			(modeFlags.feature.draggable ||
				(modeFlags.feature.coordinates &&
					modeFlags.feature.coordinates.draggable) ||
				(modeFlags.feature.coordinates &&
					modeFlags.feature.coordinates.resizable) ||
				(modeFlags.feature.coordinates &&
					typeof modeFlags.feature.coordinates.midpoints === "object" &&
					modeFlags.feature.coordinates.midpoints.draggable));

		if (!draggable) {
			return;
		}

		this.dragEventCount = 0;

		const selectedId = this.selected[0];
		const draggableCoordinateIndex = this.dragCoordinate.getDraggableIndex(
			event,
			selectedId,
		);

		// Drag Coordinate
		if (
			modeFlags &&
			modeFlags.feature &&
			modeFlags.feature.coordinates &&
			(modeFlags.feature.coordinates.draggable ||
				modeFlags.feature.coordinates.resizable) &&
			draggableCoordinateIndex !== -1
		) {
			this.setCursor(this.cursors.dragStart);

			// With resizable
			if (modeFlags.feature.coordinates.resizable) {
				this.dragCoordinateResizeFeature.startDragging(
					selectedId,
					draggableCoordinateIndex,
				);
			} else {
				// Without with resizable being set
				this.dragCoordinate.startDragging(selectedId, draggableCoordinateIndex);
			}

			setMapDraggability(false);
			return;
		}

		// Dragging Midpoint
		if (
			modeFlags &&
			modeFlags.feature &&
			modeFlags.feature.coordinates &&
			typeof modeFlags.feature.coordinates.midpoints === "object" &&
			modeFlags.feature.coordinates.midpoints.draggable
		) {
			const { clickedMidPoint: draggedMidPoint } =
				this.featuresAtMouseEvent.find(event, this.selected.length > 0);

			if (this.selected.length && draggedMidPoint) {
				// We insert the midpoint first
				this.midPoints.insert({
					featureId: selectedId,
					midPointId: draggedMidPoint.id as FeatureId,
				});

				this.onFinish(this.selected[0], {
					action: FinishActions.InsertMidpoint,
					mode: this.mode,
				});

				const draggableCoordinateIndexAfterInsert =
					this.dragCoordinate.getDraggableIndex(event, selectedId);

				this.dragCoordinate.startDragging(
					selectedId,
					draggableCoordinateIndexAfterInsert,
				);

				setMapDraggability(false);

				return;
			}
		}

		// Drag Feature
		if (
			modeFlags &&
			modeFlags.feature &&
			modeFlags.feature.draggable &&
			this.dragFeature.canDrag(event, selectedId)
		) {
			this.setCursor(this.cursors.dragStart);
			this.dragFeature.startDragging(event, selectedId);
			setMapDraggability(false);
			return;
		}
	}

	/** @internal */
	onDrag(
		event: TerraDrawMouseEvent,
		setMapDraggability: (enabled: boolean) => void,
	) {
		if (!this.allowPointerEvent(this.pointerEvents.onDrag, event)) {
			return;
		}

		const selectedId = this.selected[0];

		// If nothing selected we can return early
		if (!selectedId) {
			return;
		}

		const properties = this.readFeature.getProperties(selectedId);
		const modeFlags = this.flags[properties.mode as string];
		const canSelfIntersect: boolean =
			(modeFlags &&
				modeFlags.feature &&
				modeFlags.feature.selfIntersectable) === true;

		// Ensure drag count is incremented
		this.dragEventCount++;

		// Return if we haven't hit the drag throttle limit
		// (i.e. we only want to drag every nth event)
		if (this.dragEventCount % this.dragEventThrottle === 0) {
			return;
		}

		// Check if should rotate
		if (
			modeFlags &&
			modeFlags.feature &&
			modeFlags.feature.rotateable &&
			this.canRotate(event)
		) {
			setMapDraggability(false);
			this.rotateFeature.rotate(event, selectedId);
			return;
		}

		// Check if should scale
		if (
			modeFlags &&
			modeFlags.feature &&
			modeFlags.feature.scaleable &&
			this.canScale(event)
		) {
			setMapDraggability(false);

			this.scaleFeature.scale(event, selectedId);
			return;
		}

		if (
			this.dragCoordinateResizeFeature.isDragging() &&
			modeFlags.feature &&
			modeFlags.feature.coordinates &&
			modeFlags.feature.coordinates.resizable
		) {
			if (this.projection === "globe") {
				throw new Error(
					"Globe is currently unsupported projection for resizable",
				);
			}

			setMapDraggability(false);
			this.dragCoordinateResizeFeature.drag(
				event,
				modeFlags.feature.coordinates.resizable,
			);
			return;
		}

		// Check if coordinate is draggable and is dragged
		if (this.dragCoordinate.isDragging()) {
			const snappable = modeFlags.feature?.coordinates?.snappable;

			let snapOptions: Snapping = { toCoordinate: false };
			if (snappable === true) {
				snapOptions = { toCoordinate: true };
			} else if (typeof snappable === "object") {
				snapOptions = snappable;
			}

			this.dragCoordinate.drag(event, canSelfIntersect, snapOptions);
			return;
		}

		// Check if feature is draggable and is dragged
		if (this.dragFeature.isDragging()) {
			this.dragFeature.drag(event);
			return;
		}

		setMapDraggability(true);
	}

	/** @internal */
	onDragEnd(
		event: TerraDrawMouseEvent,
		setMapDraggability: (enabled: boolean) => void,
	) {
		if (!this.allowPointerEvent(this.pointerEvents.onDragEnd, event)) {
			return;
		}

		this.setCursor(this.cursors.dragEnd);

		// If we have finished dragging a coordinate or a feature
		// lets fire an onFinish event which can be listened to
		if (this.dragCoordinate.isDragging()) {
			this.onFinish(this.selected[0], {
				mode: this.mode,
				action: FinishActions.DragCoordinate,
			});
		} else if (this.dragFeature.isDragging()) {
			this.onFinish(this.selected[0], {
				mode: this.mode,
				action: FinishActions.DragFeature,
			});
		} else if (this.dragCoordinateResizeFeature.isDragging()) {
			this.onFinish(this.selected[0], {
				mode: this.mode,
				action: FinishActions.DragCoordinateResize,
			});
		}

		this.dragCoordinate.stopDragging();
		this.dragFeature.stopDragging();
		this.dragCoordinateResizeFeature.stopDragging();
		this.rotateFeature.reset();
		this.scaleFeature.reset();
		setMapDraggability(true);
	}

	/** @internal */
	onMouseMove(event: TerraDrawMouseEvent) {
		if (!this.selected.length) {
			this.setCursor("unset");
			return;
		}

		if (this.dragFeature.isDragging()) {
			return;
		}

		let nearbyMidPoint = false;
		this.midPoints.ids.forEach((id) => {
			if (nearbyMidPoint) {
				return;
			}
			const geometry = this.readFeature.getGeometry<Point>(id);
			const distance = this.pixelDistance.measure(event, geometry.coordinates);

			if (distance < this.pointerDistance) {
				nearbyMidPoint = true;
			}
		});

		let nearbySelectionPoint = false;
		// TODO: Is there a cleaner way to handle prioritising
		// dragging selection points?
		this.selectionPoints.ids.forEach((id: FeatureId) => {
			const geometry = this.readFeature.getGeometry<Point>(id);
			const distance = this.pixelDistance.measure(event, geometry.coordinates);
			if (distance < this.pointerDistance) {
				nearbyMidPoint = false;
				nearbySelectionPoint = true;
			}
		});

		if (nearbyMidPoint) {
			this.setCursor(this.cursors.insertMidpoint);
			return;
		}

		// If we have a feature under the pointer then show the pointer over cursor
		const { clickedFeature: featureUnderPointer } =
			this.featuresAtMouseEvent.find(event, true);

		if (
			this.selected.length > 0 &&
			((featureUnderPointer && featureUnderPointer.id === this.selected[0]) ||
				nearbySelectionPoint)
		) {
			this.setCursor(this.cursors.pointerOver);
		} else {
			// Set it back to whatever the default cursor is
			this.setCursor("unset");
		}
	}

	/** @internal */
	styleFeature(feature: GeoJSONStoreFeatures): TerraDrawAdapterStyling {
		const styles = { ...getDefaultStyling() };

		if (
			feature.properties.mode === this.mode &&
			feature.geometry.type === "Point"
		) {
			if (feature.properties[SELECT_PROPERTIES.SELECTION_POINT]) {
				styles.pointColor = this.getHexColorStylingValue(
					this.styles.selectionPointColor,
					styles.pointColor,
					feature,
				);

				styles.pointOpacity = this.getNumericStylingValue(
					this.styles.selectionPointOpacity,
					1,
					feature,
				);

				styles.pointOutlineColor = this.getHexColorStylingValue(
					this.styles.selectionPointOutlineColor,
					styles.pointOutlineColor,
					feature,
				);

				styles.pointWidth = this.getNumericStylingValue(
					this.styles.selectionPointWidth,
					styles.pointWidth,
					feature,
				);

				styles.pointOutlineOpacity = this.getNumericStylingValue(
					this.styles.selectionPointOutlineOpacity,
					1,
					feature,
				);

				styles.pointOutlineWidth = this.getNumericStylingValue(
					this.styles.selectionPointOutlineWidth,
					2,
					feature,
				);

				styles.zIndex = Z_INDEX.LAYER_THREE;

				return styles;
			}

			if (feature.properties[SELECT_PROPERTIES.MID_POINT]) {
				styles.pointColor = this.getHexColorStylingValue(
					this.styles.midPointColor,
					styles.pointColor,
					feature,
				);

				styles.pointOpacity = this.getNumericStylingValue(
					this.styles.midPointOpacity,
					1,
					feature,
				);

				styles.pointOutlineColor = this.getHexColorStylingValue(
					this.styles.midPointOutlineColor,
					styles.pointOutlineColor,
					feature,
				);

				styles.pointWidth = this.getNumericStylingValue(
					this.styles.midPointWidth,
					4,
					feature,
				);

				styles.pointOutlineOpacity = this.getNumericStylingValue(
					this.styles.midPointOutlineOpacity,
					1,
					feature,
				);

				styles.pointOutlineWidth = this.getNumericStylingValue(
					this.styles.midPointOutlineWidth,
					2,
					feature,
				);

				styles.zIndex = Z_INDEX.LAYER_FIVE;

				return styles;
			}
		} else if (feature.properties[SELECT_PROPERTIES.SELECTED]) {
			// Select mode shortcuts the styling of a feature if it is selected
			// A selected feature from another mode will end up in this block

			if (
				feature.geometry.type === "Point" &&
				feature.properties[COMMON_PROPERTIES.MARKER]
			) {
				styles.markerUrl = this.getUrlStylingValue(
					this.styles.selectedMarkerUrl,
					MARKER_URL_DEFAULT,
					feature,
				);
				styles.markerHeight = this.getNumericStylingValue(
					this.styles.selectedMarkerHeight,
					40,
					feature,
				);
				styles.markerWidth = this.getNumericStylingValue(
					this.styles.selectedMarkerWidth,
					32,
					feature,
				);
				return styles;
			} else if (feature.geometry.type === "Polygon") {
				styles.polygonFillColor = this.getHexColorStylingValue(
					this.styles.selectedPolygonColor,
					styles.polygonFillColor,
					feature,
				);

				styles.polygonOutlineWidth = this.getNumericStylingValue(
					this.styles.selectedPolygonOutlineWidth,
					styles.polygonOutlineWidth,
					feature,
				);

				styles.polygonOutlineColor = this.getHexColorStylingValue(
					this.styles.selectedPolygonOutlineColor,
					styles.polygonOutlineColor,
					feature,
				);

				styles.polygonOutlineOpacity = this.getNumericStylingValue(
					this.styles.selectedPolygonOutlineOpacity,
					1,
					feature,
				);

				styles.polygonFillOpacity = this.getNumericStylingValue(
					this.styles.selectedPolygonFillOpacity,
					styles.polygonFillOpacity,
					feature,
				);

				styles.zIndex = Z_INDEX.LAYER_ONE;
				return styles;
			} else if (feature.geometry.type === "LineString") {
				styles.lineStringColor = this.getHexColorStylingValue(
					this.styles.selectedLineStringColor,
					styles.lineStringColor,
					feature,
				);

				styles.lineStringWidth = this.getNumericStylingValue(
					this.styles.selectedLineStringWidth,
					styles.lineStringWidth,
					feature,
				);

				styles.zIndex = Z_INDEX.LAYER_ONE;
				return styles;
			} else if (feature.geometry.type === "Point") {
				styles.pointWidth = this.getNumericStylingValue(
					this.styles.selectedPointWidth,
					styles.pointWidth,
					feature,
				);

				styles.pointColor = this.getHexColorStylingValue(
					this.styles.selectedPointColor,
					styles.pointColor,
					feature,
				);

				styles.pointOpacity = this.getNumericStylingValue(
					this.styles.selectedPointOpacity,
					1,
					feature,
				);

				styles.pointOutlineColor = this.getHexColorStylingValue(
					this.styles.selectedPointOutlineColor,
					styles.pointOutlineColor,
					feature,
				);

				styles.pointOutlineOpacity = this.getNumericStylingValue(
					this.styles.selectedPointOutlineOpacity,
					1,
					feature,
				);

				styles.pointOutlineWidth = this.getNumericStylingValue(
					this.styles.selectedPointOutlineWidth,
					styles.pointOutlineWidth,
					feature,
				);

				styles.zIndex = Z_INDEX.LAYER_ONE;
				return styles;
			}
		}

		return styles;
	}

	afterFeatureUpdated(feature: GeoJSONStoreFeatures) {
		// If we have a selected feature and it has been updated
		// we need to update the selection points and midpoints
		if (this.selected.length && feature.id === this.selected[0]) {
			const flags = this.flags[feature.properties.mode as string];

			if (!flags?.feature?.coordinates) {
				return;
			}

			const type = feature.geometry.type;
			const id = feature.id as FeatureId;

			this.selectionPoints.delete();
			this.midPoints.delete();

			if (type !== "LineString" && type !== "Polygon") {
				return;
			}

			const featureCoordinates = feature.geometry.coordinates;

			this.selectionPoints.create({
				featureCoordinates,
				featureId: id,
			});

			if (flags?.feature?.coordinates?.midpoints) {
				this.midPoints.create({
					featureCoordinates,
					featureId: id,
				});
			}
		}
	}
}
